import { useCallback, useRef } from 'react';

const SECTION_IDS = ['home', 'introduction', 'products', 'contact'];

export function useSectionTransition() {
  const isTransitioning = useRef(false);

  const navigateToSection = useCallback((targetId: string) => {
    if (isTransitioning.current) return;

    const targetEl = document.getElementById(targetId);
    if (!targetEl) return;

    const viewportTop = window.scrollY;
    const viewportBottom = viewportTop + window.innerHeight;
    const targetRect = targetEl.getBoundingClientRect();
    const targetAbsTop = targetRect.top + window.scrollY;

    // If already mostly in view, just smooth scroll
    const targetCenter = targetAbsTop + targetRect.height / 2;
    if (targetCenter >= viewportTop && targetCenter <= viewportBottom) {
      const offset = 80;
      window.scrollTo({ top: targetAbsTop - offset, behavior: 'smooth' });
      return;
    }

    isTransitioning.current = true;

    // Find sections currently visible
    const allSections = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    const visibleSections = allSections.filter((section) => {
      const rect = section.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    });

    // Add fade-out to visible sections
    visibleSections.forEach((section) => {
      section.classList.add('section-transition-out');
    });

    // After fade-out, scroll instantly then fade-in
    setTimeout(() => {
      const offset = 80;
      // Use immediate scrolling without animation
      window.scrollTo({ top: targetAbsTop - offset, behavior: 'auto' });

      // Remove fade-out from all
      allSections.forEach((section) => {
        section.classList.remove('section-transition-out');
      });

      // Find newly visible sections and fade them in
      requestAnimationFrame(() => {
        const newVisible = allSections.filter((section) => {
          const rect = section.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight;
        });

        newVisible.forEach((section) => {
          section.classList.add('section-transition-in');
        });

        // Cleanup
        setTimeout(() => {
          newVisible.forEach((section) => {
            section.classList.remove('section-transition-in');
          });
          isTransitioning.current = false;
        }, 550);
      });
    }, 350);
  }, []);

  return { navigateToSection };
}